import React, { ReactNode } from 'react'
import * as Yup from 'yup'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FormikHelpers, FormikProps, isFunction } from 'formik'
import { CountryCode } from '@carsys/enums/country-code'
import {
  IEmployee,
  IOrderAccountData,
  IProduct,
  IVehicle,
  SupportTicketPart,
  SupportTicketVehicle,
  TicketContactPreference,
  TicketType
} from '@cbgms/api'
import { Form } from '@carsys/form'
import { useNotifier } from '@cbgms/base/notifier'
import { splitRegistration } from '@cbgms/base/utils/vehicle'
import { IRootReducerState } from 'state/rootReducer'
import { useSupportTicket } from './hooks'
import { EmptyUUID } from '@cbgms/base/utils/uuid'
import { hasContactPreferenceFields, hasPartFields, hasQuestionField, hasVehicleFields } from '@cbgms/components/src/support-request/utils'
import { TicketFormContextProvider } from 'app/contact/support/context/TicketFormContextProvider'
import { useProductName } from '@cbgms/components/src/order-list/hooks/use-product-name'

const defaultFormID = 'support-ticket-form'

export type SupportTicketProduct = Pick<IProduct, 'BaseID' | 'Brand' | 'CommercialName' | 'ProductGroups' | 'RecommendedQuantity'>

interface Props {
  handleOnSubmitSuccess: () => void
  /**
   * Preselected a ticket type to show related form
   * directly without user selection.
   */
  preselectedTicketType?: TicketType
  orderAccount: IOrderAccountData
  formID?: string
  shopID?: string
  shopName?: string
  vehicle?: IVehicle
  product?: SupportTicketProduct
  employee?: IEmployee
  children?: ReactNode | ((formValues: FormikProps<TicketFormValues>, isLoading: boolean) => React.ReactNode)
}

export interface CallBackDetails {
  PhoneNumber: string
  EmailAddress: string
  ContactPreference: TicketContactPreference
}

export interface TicketFormValues {
  TicketType: TicketType
  ShopID: string
  ShopName: string
  FirstName: string
  Name: string
  WholesalerName: string
  EmailAddress: string
  Question: string
  Uploads: File[]
  Part?: SupportTicketPart
  Vehicle?: SupportTicketVehicle
  CallbackDetails: CallBackDetails
}

export const TicketForm: React.FC<Props> = ({
  handleOnSubmitSuccess,
  formID = defaultFormID,
  shopID,
  shopName,
  employee,
  orderAccount,
  vehicle,
  product,
  preselectedTicketType,
  children
}) => {
  const userAccountEmail = useSelector<IRootReducerState, string | undefined>(state => state.auth?.user?.IdToken?.email)
  const userMobileNumber = useSelector<IRootReducerState, string | undefined>(state => state.auth?.employee.MobileNumber)
  const userPhoneNumber = useSelector<IRootReducerState, string | undefined>(state => state.auth?.employee.PhoneNumber)
  const shopCountry = useSelector<IRootReducerState, CountryCode>(state => state.auth.shop.VisitorCountry)
  const { countryCode, licensePlate } = splitRegistration(vehicle?.Registration)
  const { mutateAsync, isLoading } = useSupportTicket()
  const productName = useProductName(product)

  const { t } = useTranslation()
  const notifier = useNotifier()

  const onSubmitSuccess = (helpers: FormikHelpers<TicketFormValues>) => {
    handleOnSubmitSuccess()
    notifier.success(t('Contact.TicketSuccessfullySent'))
    helpers.resetForm({ values: initialValues })
  }

  const onSubmit = async (formValues: TicketFormValues) => {
    const { Question, Uploads, Vehicle, TicketType, Part, CallbackDetails } = formValues

    await mutateAsync({
      WholesalerUUID: orderAccount?.Wholesaler?.UUID || '',
      TicketType,
      Question,
      Uploads,
      Vehicle,
      Part,
      CallbackDetails
    })

    return formValues
  }

  const initialValues: TicketFormValues = {
    TicketType: preselectedTicketType || TicketType.GeneralTicket,
    ShopID: shopID || '',
    ShopName: shopName || '',
    FirstName: employee?.FirstName || '',
    Name: employee?.Name || '',
    WholesalerName: orderAccount?.Wholesaler?.Name || '',
    EmailAddress: userAccountEmail || '',
    Question: '',
    Uploads: new Array<File>(),
    Vehicle: {
      Country: countryCode || shopCountry,
      UUID: vehicle?.UUID || EmptyUUID,
      Manufacturer: vehicle?.Manufacturer || '',
      Model: vehicle?.Model || '',
      Type: vehicle?.Type || '',
      Registration: licensePlate || '',
      VIN: vehicle?.Vin || ''
    },
    Part: {
      BaseID: product?.BaseID || '',
      Description: productName || '',
      Brand: product?.Brand || '',
      Quantity: product?.RecommendedQuantity || 1
    },
    CallbackDetails: {
      PhoneNumber: userMobileNumber || userPhoneNumber || '',
      EmailAddress: userAccountEmail || '',
      ContactPreference: TicketContactPreference.EmailPreference
    }
  }

  const validationSchema = Yup.object()
    .when({
      is: (input: TicketFormValues) => hasVehicleFields(input.TicketType) && !vehicle,
      then: schema =>
        schema.shape({
          Vehicle: Yup.object().shape({
            Country: Yup.string().required(t('global.field_is_required')),
            Registration: Yup.string().required(t('global.field_is_required')),
            Manufacturer: Yup.string().required(t('global.field_is_required')),
            Model: Yup.string().required(t('global.field_is_required')),
            Type: Yup.string().required(t('global.field_is_required')),
            VIN: Yup.string()
          })
        })
    })
    .when({
      is: (input: TicketFormValues) => hasPartFields(input.TicketType) && !product,
      then: schema =>
        schema.shape({
          Part: Yup.object().shape({
            BaseID: Yup.string().required(t('global.field_is_required')),
            Description: Yup.string().required(t('global.field_is_required')),
            Brand: Yup.string().required(t('global.field_is_required')),
            Quantity: Yup.number()
              .required(t('global.field_value_min', { value: 0 }))
              .min(0)
              .max(Number.MAX_SAFE_INTEGER)
          })
        })
    })
    .when({
      is: (input: TicketFormValues) => hasContactPreferenceFields(input.TicketType),
      then: schema =>
        schema.shape({
          CallbackDetails: Yup.object().shape({
            ContactPreference: Yup.string().required(t('global.field_is_required')),
            EmailAddress: Yup.string()
              .email()
              .when('ContactPreference', {
                is: TicketContactPreference.EmailPreference,
                then: schema => schema.required(t('global.field_is_required'))
              }),
            PhoneNumber: Yup.string().when('ContactPreference', {
              is: TicketContactPreference.PhonecallPreference,
              then: schema => schema.required(t('global.field_is_required'))
            })
          })
        })
    })
    .when({
      is: (input: TicketFormValues) => hasQuestionField(input.TicketType),
      then: schema =>
        schema.shape({
          Question: Yup.string().required(t('global.field_is_required'))
        })
    })

  return (
    <Form
      formID={formID}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmitSuccess={(_, helpers) => onSubmitSuccess(helpers)}
      onSubmit={onSubmit}
      validateOnMount
    >
      {formikProps => (
        <TicketFormContextProvider>{isFunction(children) ? children(formikProps, isLoading) : children}</TicketFormContextProvider>
      )}
    </Form>
  )
}
